import React, { useCallback, useEffect, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { denormalize } from 'normalizr';

import { SCHEMAS } from 'js/api/schemas';
import { load as loadOrg } from 'js/actions/org-actions';
import { load as loadRubric } from 'js/actions/rubric-actions';
import {
  lookupOrgRubricWeightSchemes,
  updateOrgRubricWeightScheme,
} from 'js/actions/org-actions';
import { useQuery } from 'js/hooks/useQuery';

import OrgTeamRubricCustomWeights from '../components/ManageOrg/OrgTeamRubricCustomWeights';
import {
  orgManageOrgQueryId,
  orgRubricSchemesQueryId,
  teamSchemeRowById,
} from '../components/ManageOrg/orgRubricSchemesUtils';

function OrgTeamRubricCustomWeightsContainer({
  loadOrg,
  loadRubric,
  lookupOrgRubricWeightSchemes,
  updateOrgRubricWeightScheme,
}) {
  const { workspaceId, orgId, rubricId, teamId } = useParams();
  const parsedTeamId = Number(teamId);
  const orgQueryId = useMemo(() => orgManageOrgQueryId(orgId), [orgId]);

  const organization = useSelector((state) => {
    const row = state.entities.organizations[orgId];
    if (!row) return null;
    return denormalize(row, SCHEMAS.ORGANIZATION, state.entities);
  });

  const rubric = useSelector((state) => {
    const row = state.entities.rubrics[rubricId];
    if (!row) return null;
    return denormalize(row, SCHEMAS.RUBRIC, state.entities);
  });

  const loadOrgQuery = useCallback(
    (qid) => loadOrg(orgId, qid),
    [loadOrg, orgId]
  );

  const {
    refresh: refreshOrg,
    isLoading: orgQueryLoading,
    hasLoaded: orgHasLoaded,
  } = useQuery({
    queryId: orgQueryId,
    query: loadOrgQuery,
    schema: SCHEMAS.ORGANIZATION,
  });

  const schemesQueryId = useMemo(
    () => orgRubricSchemesQueryId(orgId, rubricId),
    [orgId, rubricId]
  );

  const loadSchemesQuery = useCallback(
    (qid) => lookupOrgRubricWeightSchemes(orgId, rubricId, qid),
    [lookupOrgRubricWeightSchemes, orgId, rubricId]
  );

  const {
    result: schemesPayload,
    refresh: refreshSchemes,
    isLoading: schemesLoading,
    hasLoaded: schemesHasLoaded,
  } = useQuery({
    queryId: schemesQueryId,
    query: loadSchemesQuery,
  });

  useEffect(() => {
    refreshOrg();
  }, [refreshOrg]);

  useEffect(() => {
    refreshSchemes();
  }, [refreshSchemes]);

  useEffect(() => {
    if (rubricId) {
      loadRubric(rubricId);
    }
  }, [loadRubric, rubricId]);

  const orgQueryError = useSelector(
    (state) => state.queries[orgQueryId]?.error
  );
  const orgLoading = orgQueryLoading || !orgHasLoaded;
  const orgLoadFailed = Boolean(orgQueryError);

  const teamRow = useMemo(() => {
    const map = teamSchemeRowById(schemesPayload);
    return map.get(parsedTeamId) ?? map.get(teamId);
  }, [schemesPayload, parsedTeamId, teamId]);

  const handleSaveCustomWeights = useCallback(
    async (body) => {
      await updateOrgRubricWeightScheme(orgId, rubricId, parsedTeamId, body);
      await refreshSchemes();
    },
    [updateOrgRubricWeightScheme, orgId, rubricId, parsedTeamId, refreshSchemes]
  );

  return (
    <OrgTeamRubricCustomWeights
      workspaceId={workspaceId}
      orgId={orgId}
      organization={organization}
      rubric={rubric}
      teamId={parsedTeamId}
      teamRow={teamRow}
      schemesLoading={schemesLoading || !schemesHasLoaded}
      orgLoading={orgLoading}
      orgLoadFailed={orgLoadFailed}
      onSaveCustomWeights={handleSaveCustomWeights}
    />
  );
}

export default connect(null, {
  loadOrg,
  loadRubric,
  lookupOrgRubricWeightSchemes,
  updateOrgRubricWeightScheme,
})(OrgTeamRubricCustomWeightsContainer);
